import React, { Component } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {
  findIndex,
  get,
  isFunction,
  isNumber,
  isObjectLike,
  noop,
  omit
} from 'lodash'

import styles from './Select.module.scss'

const defaultProps = {
  optionKey: 'key',
  optionValue: 'value',
  onChange: noop
}

const objectLike = PropTypes.instanceOf(Object)
const keyLikes = [PropTypes.string, PropTypes.number]
const oneOfKeyLikes = PropTypes.oneOfType(keyLikes)

const propTypes = {
  // options are REQUIRED in this implementation
  options: PropTypes.arrayOf(objectLike).isRequired,
  onChange: PropTypes.func,

  selected: PropTypes.oneOfType([...keyLikes, objectLike]),

  // Used to specify alternative keys for default render method
  optionKey: oneOfKeyLikes,

  // Used to specify optional value to render in the item. If a function,
  // will be called with the object for rendering (while leaving default
  // option rendering in place)
  optionValue: PropTypes.oneOfType([...keyLikes, PropTypes.func]),

  // Optional override for rendering label. Falls back to optionValue
  renderLabel: PropTypes.func,

  // NON CONFIGURABLE, PASSED FROM CONTAINER
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  native: PropTypes.bool.isRequired
}

class Select extends Component {
  static propTypes = propTypes
  static defaultProps = defaultProps

  constructor (props) {
    super(props)

    this.state = {
      open: false,
      focused: this.getFocusedIndex()
    }

    this.el = React.createRef()
  }

  // State functions
  getFocusedIndex () {
    const { optionKey, options, selected } = this.props
    const key = isObjectLike(selected) ? selected[optionKey] : selected
    const index = findIndex(options, { [optionKey]: key })
    return Math.max(0, index)
  }

  setOpen (open = !this.state.open) {
    const updatedState = { open }

    if (!open) {
      updatedState.focused = this.getFocusedIndex()
    }

    this.setState(updatedState)
  }

  setFocused = focused => {
    if (!isNumber(focused) || this.state.focused === focused) {
      return
    }

    this.setState({ focused })
  }

  adjustFocused = i => {
    const { options } = this.props
    const focused = this.state.focused + i

    if (isNumber(focused) && focused >= 0 && focused < options.length) {
      this.setState({ focused })
    }
  }

  onChangeHandler = option => e => {
    e.preventDefault()
    const { onChange, optionKey, selected } = this.props

    this.el.current.blur()
    onChange(isObjectLike(selected) ? option : option[optionKey])
  }

  onMouseDown = e => {
    const { open } = this.props

    if (open) {
      e.preventDefault()
      e.target.blur()
    }
  }

  onKeyHandler = e => {
    e.preventDefault()
    const { options } = this.props
    const { focused } = this.state

    switch (e.key) {
      case 'ArrowDown':
        this.adjustFocused(1)
        break
      case 'ArrowUp':
        this.adjustFocused(-1)
        break
      case 'Enter':
        this.onChangeHandler(options[focused])(e)
        break
      default:
        break
    }
  }

  getSelectedItem () {
    const { options } = this.props
    return options.find(this.isSelected)
  }

  isSelected = option => {
    const { selected, optionKey } = this.props

    if (!selected || !optionKey) {
      return false
    }

    return (
      selected === option[optionKey] ||
      selected[optionKey] === option[optionKey]
    )
  }

  renderOption = ({ key, value, option }, i) => {
    const { focused } = this.state
    const isSelected = this.isSelected(option)
    const optionClass = classnames(styles.option, {
      [styles.selectedOption]: isSelected,
      [styles.focusedOption]: i === focused
    })

    return (
      <li
        key={key}
        className={optionClass}
        role='option'
        onMouseDown={this.onChangeHandler(option)}
        onMouseEnter={() => this.setFocused(i)}
        aria-selected={isSelected}
      >
        {value}
      </li>
    )
  }

  getOptionRenderer () {
    const { optionValue } = this.props
    return isFunction(optionValue)
      ? optionValue
      : o => get(o, optionValue, null)
  }

  renderChildren () {
    const { optionKey, options } = this.props
    const renderer = this.getOptionRenderer()

    return options.map((o, i) =>
      this.renderOption(
        {
          key: o[optionKey],
          value: renderer(o),
          option: o
        },
        i
      )
    )
  }

  renderLabel () {
    const { renderLabel } = this.props
    const selected = this.getSelectedItem()
    const renderer = renderLabel || this.getOptionRenderer()

    return renderer(selected)
  }

  render () {
    const { className, open } = this.props
    const passedProps = omit(this.props, 'children', ...Object.keys(propTypes))

    const innerClass = classnames(styles.innerContainer, {
      [styles.open]: open
    })

    return (
      <div className={styles.outerContainer}>
        <div
          {...passedProps}
          className={classnames(styles.select, className, 'component__select')}
          role='button'
          tabIndex='0'
          onFocus={() => this.props.setOpen(true)}
          onBlur={() => this.props.setOpen(false)}
          onMouseDown={this.onMouseDown}
          onKeyDown={this.onKeyHandler}
          ref={this.el}
        >
          {this.renderLabel()}
        </div>
        <ul
          className={innerClass}
          children={this.renderChildren()}
          role='listbox'
        />
      </div>
    )
  }
}

export default Select
